import Button from "@/components/atom/Button";
import Input, { Caption } from "@/components/atom/Input";
import AlertGon from "@/components/sub-atom/Alert";
import currency from "@/functions/currency";
import {
  addCarts,
  getFeeAdmin,
  getVouchers,
  revalidateCustom,
} from "@/functions/databaseData";
import { ArrowRightSvg, VoucherOutlineSvg } from "@/public/svg";
import { usePathname, useRouter } from "next/navigation";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useCheckBreakPoint } from "@/functions/usResponsive";
import useDelayedRender from "@/functions/useDelayedRender";
import Link from "next/link";

export default function Rincian({ datas, session, carts, overStock }) {
  const path = usePathname();
  const queryClient = useQueryClient();
  const shouldRendered = useDelayedRender(300);

  const router = useRouter();
  const [loading, setLoading] = useState({});
  const [chooseVoucher, setChooseVoucher] = useState({
    title: carts?.code_voucher,
  });
  const reduceJumlahProduk = datas.reduce(
    (accumulator, [_, item]) => accumulator + item.jumlah,
    0
  );

  useEffect(() => {
    setChooseVoucher({ error: false, title: carts?.code_voucher });
  }, [carts?.code_voucher]);

  async function handleCheckout() {
    try {
      setLoading((old) => ({ ...old, handleCheckout: true }));
      if (!carts?.fee_admin || Number(carts?.fee_admin) <= 0) {
        const feeAdmin = await getFeeAdmin();
        let res = await addCarts({
          body: {
            id: session?.user?.data?.cart_id,
            fee_admin: feeAdmin?.fee_admin,
            total_pembayaran: carts?.total_pembayaran + feeAdmin?.fee_admin,
            change: "reset",
          },
        });
      }
      revalidateCustom({
        path: "/cart/checkout",
      });
      const section = document.getElementById("btn-checkout");
      if (section) section.click();
    } catch (error) {
      console.log("when checkout : ", error?.message);
    } finally {
      setLoading((old) => ({ ...old, handleCheckout: false }));
    }
  }

  async function handleAddVoucher() {
    try {
      if (!chooseVoucher?.title) throw new Error("Masukkan kode voucher");

      const datasVoucher =
        (await getVouchers({
          token: session?.user?.token,
          cart_id: session?.user?.data?.cart_id,
        })) || [];
      const isListVoucher = datasVoucher?.filter((val) => !val?.claimed);
      if (isListVoucher?.length <= 0) throw new Error("Voucher not found");

      const val = isListVoucher?.filter(
        (val) => val?.code_voucher == chooseVoucher?.title
      )?.[0];

      if (!val) throw new Error("Kode voucher tidak ditemukan");

      if (val?.minimum_voucher > carts?.total_tagihan_normal)
        throw new Error(
          `Minimal belanja ${currency(chooseVoucher?.minimum_voucher)}`
        );

      let res = await addCarts({
        body: {
          id: carts?._id,
          code_voucher: val?.title,
          nilai_voucher: val?.nominal_voucher,
          total_pembayaran:
            carts?.total_pembayaran - val?.nominal_voucher <= 0
              ? 0
              : carts?.total_pembayaran - val?.nominal_voucher,
          change: "vouchers",
        },
      });
      queryClient.setQueryData(["carts"], (old) => res);
      setChooseVoucher({
        error: "",
        ...val,
      });
    } catch (error) {
      setChooseVoucher((old) => ({
        title: old?.title,
        error: error?.message,
      }));
    }
  }

  const breakpoint = useCheckBreakPoint();

  return (
    shouldRendered && (
      <>
        {/* rincian */}
        {breakpoint == "ui-laptop" ? (
          <div className="w-full gap-4 px-6 py-4 flex items-start justify-between  bg-shadesGON-50 shadow-mediumGON rounded-lg">
            <div className="flex flex-col gap-2 w-full max-w-[40%] items-center">
              <div id="voucher" className="flex gap-2 w-full">
                <Input
                  value={chooseVoucher?.title ?? ""}
                  placeholder={
                    loading?.getDataVouchers
                      ? "loading..."
                      : "Masukkan kode Voucher"
                  }
                  disabled={overStock > 0 || loading?.getDataVouchers}
                  onChange={(value) => setChooseVoucher({ title: value })}
                  icon={{
                    left: (props) => <VoucherOutlineSvg {...props} />,
                  }}
                  className="border-greyscaleGON-200"
                />
                <div
                  className={twMerge(
                    chooseVoucher?.title?.length > 0
                      ? "border-primaryGON-100"
                      : "border-primaryGON-50",
                    "border  p-3 rounded cursor-pointer"
                  )}
                  onClick={handleAddVoucher}
                >
                  <ArrowRightSvg
                    className={twMerge(
                      chooseVoucher?.title?.length > 0
                        ? "text-primaryGON-400"
                        : "text-primaryGON-50",
                      "h-5 w-5"
                    )}
                  />
                </div>
              </div>
              {chooseVoucher?.error?.length > 0 && (
                <Caption error={true} caption={chooseVoucher?.error} />
              )}
            </div>
            <div className="flex justify-between gap-1 w-full max-w-[50%]">
              <div
                id="jumlah-produk"
                className="flex flex-col gap-1 w-full max-w-[292px]"
              >
                <p className="p1-regular text-greyscaleGON-400">
                  Jumlah Produk
                </p>
                <p className="h6-semiBold text-greyscaleGON-700">
                  {reduceJumlahProduk} pcs
                </p>
              </div>
              <div
                id="berat-total"
                className="flex flex-col gap-1 w-full max-w-[292px]"
              >
                <p className="p1-regular text-greyscaleGON-400">Berat Total</p>
                <p className="h6-semiBold text-greyscaleGON-700">
                  {!carts?.weight ? 0 : carts?.weight} gr
                </p>
              </div>
              <div
                id="total-harga"
                className="flex flex-col gap-1 w-full max-w-[292px]"
              >
                <p className="p1-regular text-greyscaleGON-400">Total Harga</p>
                {carts?.nilai_voucher ? (
                  <>
                    <p className="h6-semiBold text-primaryGON-400">
                      {currency(carts?.total_pembayaran - carts?.nilai_voucher)}
                    </p>
                    <p className="p2-semiBold text-greyscaleGON-400 line-through">
                      {currency(carts?.total_pembayaran)}
                    </p>
                  </>
                ) : (
                  <p className="h6-semiBold text-primaryGON-400">
                    {currency(carts?.total_pembayaran)}
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full flex flex-col gap-4">
            <div className="w-full gap-2 px-6 py-4 flex bg-shadesGON-50 shadow-mediumGON rounded-lg">
              <div className="flex flex-col gap-2 w-full items-center">
                <div id="voucher" className="flex gap-2 w-full">
                  <Input
                    value={chooseVoucher?.title ?? ""}
                    placeholder={
                      loading?.getDataVouchers
                        ? "loading..."
                        : "Masukkan kode Voucher"
                    }
                    disabled={overStock > 0 || loading?.getDataVouchers}
                    onChange={(value) => setChooseVoucher({ title: value })}
                    icon={{
                      left: (props) => <VoucherOutlineSvg {...props} />,
                    }}
                    className="border-greyscaleGON-200"
                  />
                  <div
                    className={twMerge(
                      chooseVoucher?.title?.length > 0
                        ? "border-primaryGON-100"
                        : "border-primaryGON-50",
                      "border  p-3 rounded cursor-pointer"
                    )}
                    onClick={handleAddVoucher}
                  >
                    <ArrowRightSvg
                      className={twMerge(
                        chooseVoucher?.title?.length > 0
                          ? "text-primaryGON-400"
                          : "text-primaryGON-50",
                        "h-5 w-5"
                      )}
                    />
                  </div>
                </div>
                {chooseVoucher?.error?.length > 0 && (
                  <Caption error={true} caption={chooseVoucher?.error} />
                )}
              </div>
            </div>
            <div className="w-full gap-2 px-6 py-4 flex flex-col  bg-shadesGON-50 shadow-mediumGON rounded-lg">
              <div id="jumlah-produk" className="flex gap-1 justify-between">
                <p className="p1-regular text-greyscaleGON-400">
                  Jumlah Produk
                </p>
                <p className="h6-semiBold text-greyscaleGON-700">
                  {reduceJumlahProduk} pcs
                </p>
              </div>
              <div id="berat-total" className="flex gap-1 justify-between">
                <p className="p1-regular text-greyscaleGON-400">Berat Total</p>
                <p className="h6-semiBold text-greyscaleGON-700">
                  {!carts?.weight ? 0 : carts?.weight} gr
                </p>
              </div>
              <div
                id="total-harga"
                className="flex gap-1 justify-between items-start"
              >
                <p className="p1-regular text-greyscaleGON-400">Total Harga</p>
                {carts?.nilai_voucher ? (
                  <div className="flex flex-col-reverse items-end">
                    <p className="h6-semiBold text-primaryGON-400">
                      {currency(carts?.total_pembayaran - carts?.nilai_voucher)}
                    </p>
                    <p className="p2-semiBold text-greyscaleGON-400 line-through">
                      {currency(carts?.total_pembayaran)}
                    </p>
                  </div>
                ) : (
                  <p className="h6-semiBold text-primaryGON-400">
                    {currency(carts?.total_pembayaran)}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {overStock > 0 && (
          <div className="w-full">
            <AlertGon
              title={`Oops, ada ${overStock} barang yang tidak bisa diproses.`}
              colors="error"
            />
          </div>
        )}

        {/* konfirmasi */}
        <div className="w-full flex flex-col gap-2 items-end pb-10">
          <div className="w-full md:max-w-[368px]">
            <Link
              className="sr-only"
              href={"/cart/checkout"}
              id="btn-checkout"
              shallow
            />
            <Button
              id="checkout"
              loading={loading?.handleCheckout}
              onClick={handleCheckout}
              label={`Checkout (${reduceJumlahProduk})`}
              disable={
                datas?.length <= 0 ||
                (datas?.length == 1 && overStock > 0) ||
                loading?.handleCheckout
              }
            />
          </div>
          <div className="w-full md:max-w-[368px]">
            <Link href={"/"} shallow aria-disabled={loading.handleCheckout}>
              <Button
                id="tambah-produk"
                label="Tambah Produk"
                variants="outline"
                colors="primary"
              />
            </Link>
          </div>
        </div>
      </>
    )
  );
}
